import { fetchJson } from './util/fetch-json';

type StreamStatus = {
    isLive: boolean;
    title: string | null;
    gameBoxArtUrl: string | null;
    viewerCount: number | null;
    startedAt: string | null;
};

const POLL_INTERVAL = 10_000;
const TICK_INTERVAL = 30_000;
const FETCH_TIMEOUT = 8_000;
const numberFormat = new Intl.NumberFormat('nl-NL');

const isStreamStatus = (value: unknown): value is StreamStatus => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const status = value as Record<string, unknown>;

    return typeof status.isLive === 'boolean'
        && (status.title === null || typeof status.title === 'string')
        && (status.gameBoxArtUrl === null || typeof status.gameBoxArtUrl === 'string')
        && (status.viewerCount === null || typeof status.viewerCount === 'number')
        && (status.startedAt === null || typeof status.startedAt === 'string');
};

export function initStreamStatus(): void {
    const root = document.querySelector<HTMLElement>('[data-swl-stream-status]');
    const endpoint = root?.dataset.endpoint;

    if (!root || !endpoint) {
        return;
    }

    const announceEl = root.querySelector<HTMLElement>('[data-swl-stream-announce]');
    const bandEl = root.querySelector<HTMLElement>('[data-swl-stream-band]');
    const boxArtEl = root.querySelector<HTMLImageElement>('[data-swl-stream-boxart]');
    const titleEl = root.querySelector<HTMLElement>('[data-swl-stream-title]');
    const viewersEl = root.querySelector<HTMLElement>('[data-swl-stream-viewers]');
    const viewersValueEl = root.querySelector<HTMLElement>('[data-swl-stream-viewers-value]');
    const sepEl = root.querySelector<HTMLElement>('[data-swl-stream-sep]');
    const uptimeEl = root.querySelector<HTMLElement>('[data-swl-stream-uptime]');

    let startedAt = root.dataset.startedAt || null;
    let lastIsLive = root.classList.contains('is-live');

    const computeUptime = (): string | null => {
        if (!startedAt) {
            return null;
        }

        const startMs = new Date(startedAt).getTime();

        if (Number.isNaN(startMs)) {
            return null;
        }

        const elapsed = Math.max(0, Date.now() - startMs);
        const totalMinutes = Math.floor(elapsed / 60_000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return hours > 0 ? `${hours}u ${minutes}m` : `${minutes}m`;
    };

    const renderUptime = (): string | null => {
        const uptime = computeUptime();

        uptimeEl?.toggleAttribute('hidden', !uptime);

        if (uptimeEl && uptime) {
            const label = `${uptime} live`;

            if (uptimeEl.textContent !== label) {
                uptimeEl.textContent = label;
            }
        }

        return uptime;
    };

    const render = (status: StreamStatus): void => {
        root.classList.toggle('is-live', status.isLive);
        startedAt = status.startedAt;

        if (status.isLive !== lastIsLive) {
            lastIsLive = status.isLive;

            if (announceEl) {
                announceEl.textContent = status.isLive ? 'Nu live' : 'Offline';
            }
        }

        if (bandEl) {
            bandEl.toggleAttribute('inert', !status.isLive);

            if (status.isLive) {
                bandEl.removeAttribute('aria-hidden');
            } else {
                bandEl.setAttribute('aria-hidden', 'true');
            }
        }

        const hasBoxArt = !!status.gameBoxArtUrl;
        boxArtEl?.toggleAttribute('hidden', !hasBoxArt);

        if (boxArtEl) {
            if (hasBoxArt) {
                if (boxArtEl.getAttribute('src') !== status.gameBoxArtUrl) {
                    boxArtEl.src = status.gameBoxArtUrl as string;
                }
            } else {
                boxArtEl.removeAttribute('src');
            }
        }

        const hasTitle = !!status.title;
        titleEl?.toggleAttribute('hidden', !hasTitle);

        if (titleEl && hasTitle && titleEl.textContent !== status.title) {
            titleEl.textContent = status.title;
        }

        const hasViewers = status.viewerCount != null;
        viewersEl?.toggleAttribute('hidden', !hasViewers);

        if (viewersValueEl && hasViewers) {
            const formatted = numberFormat.format(status.viewerCount as number);

            if (viewersValueEl.textContent !== formatted) {
                viewersValueEl.textContent = formatted;
            }
        }

        const uptime = renderUptime();
        sepEl?.toggleAttribute('hidden', !(hasViewers && uptime));
    };

    let inFlight = false;

    const refresh = async (): Promise<void> => {
        if (inFlight) {
            return;
        }

        inFlight = true;

        try {
            const payload = await fetchJson(endpoint, FETCH_TIMEOUT);

            if (isStreamStatus(payload)) {
                render(payload);
            }
        } finally {
            inFlight = false;
        }
    };

    let pollTimer: number | undefined;
    let tickTimer: number | undefined;

    const startTimers = (): void => {
        if (pollTimer !== undefined) {
            return;
        }

        pollTimer = window.setInterval(refresh, POLL_INTERVAL);
        tickTimer = window.setInterval(renderUptime, TICK_INTERVAL);
    };

    const stopTimers = (): void => {
        window.clearInterval(pollTimer);
        window.clearInterval(tickTimer);
        pollTimer = undefined;
        tickTimer = undefined;
    };

    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            stopTimers();

            return;
        }

        void refresh();
        renderUptime();
        startTimers();
    });

    if (!document.hidden) {
        void refresh();
        startTimers();
    }
}
